import React, { useEffect } from 'react';
import {
  CircularProgress,
  Alert,
} from '@mui/material';
import { type GridColDef } from '@mui/x-data-grid';
import { DataGrid, PageTitle, ListLayoutPanel, AddButton, GridEditButton } from '../../../components/common';
import { EditEncargado } from './components/EditEncargado';
import { type Encargado, type EncargadoRequest } from './types/encargado';
import { useEncargadoStore } from './store/encargadoStore';

const EncargadosPage: React.FC = () => {
  const {
    encargados,
    loading,
    error,
    selectedEncargado,
    dialogOpen,
    fetchEncargados,
    saveEncargado,
    setSelectedEncargado,
    openDialog,
    closeDialog,
    clearError
  } = useEncargadoStore();

  const columns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 80 },
    { field: 'nombres', headerName: 'Nombres', width: 200, flex: 1 },
    { field: 'apellidos', headerName: 'Apellidos', width: 200, flex: 1 },
    { field: 'numeroDocumento', headerName: 'Nº Documento', width: 150 },
    { field: 'tipoDocumento', headerName: 'Tipo Doc.', width: 120 },
    {
      field: 'isActivo',
      headerName: 'Estado',
      width: 100,
      renderCell: (params) => (
        <span style={{ color: params.row.isActivo ? 'green' : 'red' }}>
          {params.row.isActivo ? 'Activo' : 'Inactivo'}
        </span>
      ),
    },
    {
      field: 'actions',
      headerName: '',
      width: 80,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <GridEditButton
          onClick={() => handleEdit(params.row.id)}
          ariaLabel={`Editar encargado ${params.row.nombres} ${params.row.apellidos}`}
        />
      ),
    },
  ];

  useEffect(() => {
    fetchEncargados();
  }, [fetchEncargados]);

  const handleEdit = (id: number) => {
    const encargado = encargados.find(e => e.id === id) || null;
    setSelectedEncargado(encargado);
    openDialog();
  };

  const handleCreate = () => {
    setSelectedEncargado(null);
    openDialog();
  };

  const handleClose = () => {
    closeDialog();
  };

  const handleSave = async (updatedEncargado: EncargadoRequest) => {
    try {
      await saveEncargado(updatedEncargado);
    } catch (error) {
      console.error('Error saving encargado:', error);
    }
  };

  if (loading) {
    return (
      <ListLayoutPanel>
        <PageTitle
          title="Encargados"
          subtitle="Gestión de encargados"
          actions={
            <AddButton
              text="Nuevo Encargado"
              onClick={handleCreate}
            />
          }
        />
        <CircularProgress />
      </ListLayoutPanel>
    );
  }

  if (error) {
    return (
      <ListLayoutPanel>
        <PageTitle
          title="Encargados"
          subtitle="Gestión de encargados"
          actions={
            <AddButton
              text="Nuevo Encargado"
              onClick={handleCreate}
            />
          }
        />
        <Alert
          severity="error"
          onClose={clearError}
        >
          {error}
        </Alert>
      </ListLayoutPanel>
    );
  }

  return (
    <ListLayoutPanel>
      <PageTitle
        title="Encargados"
        subtitle="Gestión de encargados"
        actions={
          <AddButton
            text="Nuevo Encargado"
            onClick={handleCreate}
          />
        }
      />

      <DataGrid
        columns={columns}
        rows={encargados}
        getRowId={(row: Encargado) => row.id}
      />

      <EditEncargado
        open={dialogOpen}
        encargado={selectedEncargado}
        onClose={handleClose}
        onSave={handleSave}
        title={selectedEncargado ? "Editar Encargado" : "Nuevo Encargado"}
      />
    </ListLayoutPanel>
  );
};

export default EncargadosPage;
